// src/components/ObradForm.jsx
import React, { useMemo, useState } from "react";
import { getOrderId } from "../lib/orderId";
import { useFormData } from "../context/FormDataContext";
import { POSADKY } from "../data/posadky";

import { apiFetch as authApiFetch, logout as authLogout } from "../auth/authClient";

import { AUTA_SPZ } from "../data/auta";
const inputStyle = { width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #444", outline: "none" };
const labelStyle = { fontSize: 13, fontWeight: 900, marginBottom: 6 };
const btnStyle = { padding: "10px 14px", borderRadius: 10, border: "1px solid #444", cursor: "pointer", fontWeight: 900 };

const safeStr = (v) => (v == null ? "" : String(v));
const pick = (...vals) => {
  for (const v of vals) {
    const s = safeStr(v).trim();
    if (s) return s;
  }
  return "";
};

// ✅ jednotné volání API přes authClient (token + base URL)
async function apiJson(path, opts = {}) {
  try {
    const headersIn = (opts && typeof opts === "object" ? opts.headers : null) || {};
    const headers = { "Content-Type": "application/json", ...headersIn };

    return await authApiFetch(path, {
      ...opts,
      headers,
    });
  } catch (e) {
    if (e?.status === 401) authLogout();
    throw e;
  }
}

export default function ObradForm(props) {
  // ✅ ber currentOrderId mimo formData (stejně jako v KalendarObrady)
  const { formData, setFormData, currentOrderId } = useFormData();
  const orderId = pick(currentOrderId, getOrderId({ formData, propsOrderId: props?.orderId }), formData?.id, "");

  
  const vstup = formData?.vstupniInformace || {};
  const z = formData?.zesnulaOsoba || {};
  const obrad = formData?.obrad || {};

  // ✅ tohle je ID zakázky, které musí jít do kalendáře
  // pro kalendář
  const zesnulyText = useMemo(() => {
    const j = pick(z?.jmeno, vstup?.jmenoZesnuleho, vstup?.jmeno);
    const p = pick(z?.prijmeni, vstup?.prijmeniZesnuleho, vstup?.prijmeni);
    return [p, j].filter(Boolean).join(" ").trim();
  }, [z, vstup]);

  const [local, setLocal] = useState({
    typ: safeStr(obrad?.typ || "Kremace"),
    datum: safeStr(obrad?.datum || ""),
    cas: safeStr(obrad?.cas || ""),
    vystav: safeStr(obrad?.vystav || ""),
    poznamka: safeStr(obrad?.poznamka || ""),
    posadka: safeStr(obrad?.posadka || obrad?.posadkaNazev || ""),
    casOd: safeStr(obrad?.casOd || obrad?.cas || "10:00"),
    casDo: safeStr(obrad?.casDo || "11:00"),
  });

  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [kolize, setKolize] = useState(null); // { warning, blocking, collisions }
  const [showCrewPicker, setShowCrewPicker] = useState(false);
  const [showAutoPicker, setShowAutoPicker] = useState(false);

  const selectedCrew = useMemo(() => {
    return POSADKY.find((p) => p.nazev === local.posadka) || null;
  }, [local.posadka]);

  
  const posadkaFinal = String(local.posadka || local.posadkaRucne || "").trim();
function setField(k, v) {
    setLocal((s) => ({ ...s, [k]: v }));
  }

  async function validateCollision(payload) {
    return apiJson("/api/obrady/validate", { method: "POST", body: JSON.stringify(payload) });
  }

  async function saveCalendar(payload) {
    return apiJson("/api/obrady", { method: "POST", body: JSON.stringify(payload) });
  }

  // ===========================
  // KOLIZE LIDÍ (ne jen posádky)
  // ===========================
  function timeToMin(t) {
    const m = String(t || "").trim().match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return null;
    const hh = Number(m[1]), mm = Number(m[2]);
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
    return hh * 60 + mm;
  }

  function timeOverlap(aFrom, aTo, bFrom, bTo) {
    const A1 = timeToMin(aFrom), A2 = timeToMin(aTo);
    const B1 = timeToMin(bFrom), B2 = timeToMin(bTo);
    if (A1 == null || A2 == null || B1 == null || B2 == null) return false;
    // překryv intervalů (A1..A2) a (B1..B2)
    return Math.max(A1, B1) < Math.min(A2, B2);
  }

  async function loadDayItems(datum) {
    const d = String(datum || "").trim();
    if (!d) return [];
    const raw = await apiJson(`/api/obrady?from=${encodeURIComponent(d)}&to=${encodeURIComponent(d)}`);
      // NORMALIZE_OBRADY_V2 – backend může vracet {ok:true, items:[...]} nebo rovnou [...]
      const itemsRaw =
        Array.isArray(raw) ? raw :
        Array.isArray(raw?.items) ? raw.items :
        Array.isArray(raw?.obrady) ? raw.obrady :
        Array.isArray(raw?.data) ? raw.data :
        [];
      const data = itemsRaw.map((o) => ({
        id: o?.id ?? "",
        datum: o?.datum ?? "",
        casOd: o?.casOd ?? "10:00",
        casDo: o?.casDo ?? "11:00",
        misto: o?.misto ?? "",
        typ: o?.typ ?? "",
        zesnuly: o?.zesnuly ?? "",
        posadkaNazev: o?.posadkaNazev ?? "",
        posadkaLide: Array.isArray(o?.posadkaLide) ? o.posadkaLide : [],
        stav: o?.stav ?? "draft",
        objednavkaId: o?.objednavkaId ?? o?.orderId ?? "",
        poznamka: o?.poznamka ?? "",
      }));

    return data;
  }

  async function findPeopleCollisions({ datum, casOd, casDo, people, ignoreId }) {
    const ppl = Array.isArray(people)
      ? people.map((x) => String(x || "").trim()).filter(Boolean)
      : [];
    if (!ppl.length) return [];

    const items = await loadDayItems(datum);
    const out = [];

    for (const it of items) {
      const itId = String(it?.id || "");
      if (ignoreId && itId && String(ignoreId) === itId) continue;

      const itFrom = String(it?.casOd || "").trim();
      const itTo   = String(it?.casDo || "").trim();
      if (!timeOverlap(casOd, casDo, itFrom, itTo)) continue;

      const itPeople = Array.isArray(it?.posadkaLide)
        ? it.posadkaLide.map((x) => String(x || "").trim()).filter(Boolean)
        : [];

      const overlap = ppl.filter((x) => itPeople.includes(x));
      if (overlap.length) {
        out.push({
          id: itId,
          datum: String(it?.datum || ""),
          casOd: itFrom,
          casDo: itTo,
          zesnuly: String(it?.zesnuly || ""),
          posadkaNazev: String(it?.posadkaNazev || ""),
          lide: overlap,
        });
      }
    }
    return out;
  }


  // ===========================
  

  // ✅ 1) jen uložit do formData (bez kalendáře)
  async function handleSaveOnlyForm() {
    setMsg("");
    setKolize(null);
    setShowCrewPicker(false);
      setShowAutoPicker(false);

    const datum = safeStr(local.datum).trim();
    const casOd = safeStr(local.casOd).trim();
    const casDo = safeStr(local.casDo).trim();

    if (!datum || !casOd || !casDo) {
      setMsg("Doplň datum + čas od + čas do.");
      return;
    }

    const obradData = {
      ...obrad,
        // ✅ místo obřadu (kvůli Převozu: formData.obrad.misto)
        misto: String(local.vystav || local.misto || "").trim(),
      typ: local.typ,
      datum,
      cas: local.cas,
      vystav: local.vystav,
      poznamka: local.poznamka,
      posadka: local.posadka,
        posadkaNazev: posadkaFinal,
        posadkaRucne: String(local.posadkaRucne || "").trim(),
        autoSpz: String(formData?.obrad?.autoSpz || "").trim(),
      posadkaLide: selectedCrew?.lide || [],
      casOd,
      casDo,
    };

    setFormData((prev) => ({ ...(prev || {}), obrad: obradData }));
    setMsg("Uloženo (jen v objednávce).");
  }

  // ✅ 2) uložit do kalendáře (a zároveň do formData)
  async function handleSaveToCalendar() {
    setMsg("");
    setKolize(null);
    setShowCrewPicker(false);

    const datum = safeStr(local.datum).trim();
    const casOd = safeStr(local.casOd).trim();
    const casDo = safeStr(local.casDo).trim();

    if (!datum || !casOd || !casDo) {
      setMsg("Doplň datum + čas od + čas do.");
      return;
    }
    if (!posadkaFinal) {
      setMsg("Vyber posádku (nebo ji napiš ručně).");
      return;
    }
    if (!orderId) {
      setMsg("Chybí ID zakázky (currentOrderId). Otevři/načti zakázku z knihy a pak ukládej obřad.");
      return;
    }

    setSaving(true);
    try {
      // 1) validace kolize
      
        // 0) kolize lidí (stejný člověk nesmí být na 2 obřadech ve stejný čas)
        const people = selectedCrew?.lide || [];
        const peopleCollisions = await findPeopleCollisions({
          datum,
          casOd,
          casDo,
          people,
          ignoreId: (obrad?.calendarId || obrad?.id || null),
        });

        if (peopleCollisions.length) {
          const first = peopleCollisions[0];
          setKolize({
            blocking: `Kolize lidí: ${first.lide.join(", ")} už jsou v obřadu ${first.datum} ${first.casOd}-${first.casDo} (${first.zesnuly || "bez jména"})`,
            collisions: peopleCollisions,
          });
          setShowCrewPicker(true);
          setShowAutoPicker(!!autoSpzVal);
          setMsg(`Kolize lidí: ${first.lide.join(", ")} – vyber jinou posádku.`);
          setSaving(false);
          return;
        }

const autoSpzVal = String(formData?.obrad?.autoSpz || "").trim();
      const autoSpzVal2 = String(formData?.obrad?.autoSpz || obrad?.autoSpz || "").trim();
      const validatePayload = {
        datum,
        casOd,
        casDo,
        posadkaNazev: posadkaFinal,
        posadkaRucne: String(local.posadkaRucne || "").trim(),
        id: obrad?.calendarId || obrad?.id || null,
      ...(autoSpzVal ? { autoSpz: autoSpzVal } : {}),
      };

      const v = await validateCollision(validatePayload);

      if (v?.blocking) {
        const msgAuto =
          (v?.conflicts || []).some((c) =>
            String(c?.reason || "").toUpperCase().includes("AUTO") ||
            String(c?.reason || "").toUpperCase().includes("SPZ")
          );

        setKolize(v);
        setShowCrewPicker(true);

        if (msgAuto) {
          setShowAutoPicker(true);
          setMsg("🚗 Kolize auta (SPZ) – vyber jiné auto nebo bez auta.");
        } else {
          setMsg(v.blocking || "Kolize se stejnou posádkou");
        }

        setSaving(false);
        return;
      }

      if (v?.warning) setKolize(v);

      // 2) uložit do FormData
      const obradData = {
        ...obrad,
        typ: local.typ,
        datum,
        cas: local.cas,
        vystav: local.vystav,
        poznamka: local.poznamka,
        posadkaNazev: posadkaFinal,
        posadkaRucne: String(local.posadkaRucne || "").trim(),
        posadkaLide: selectedCrew?.lide || [],
        casOd,
        casDo,
      };

      // 3) uložit do kalendáře (server)
      const calendarPayload = {

        id: obrad?.calendarId || obrad?.id || null,
        datum,
        casOd,
        casDo,
        misto: local.vystav || "",
        typ: local.typ || "",
        zesnuly: zesnulyText || "",
        posadkaNazev: local.posadka,
        posadkaLide: selectedCrew?.lide || [],
        stav: "potvrzeno",
        objednavkaId: orderId, // ✅ TADY JE TO HLAVNÍ
        poznamka: local.poznamka || "",
  ...(autoSpzVal2 ? { autoSpz: autoSpzVal2 } : {}),
};

      const saved = await saveCalendar(calendarPayload);
      const calendarId = saved?.item?.id || calendarPayload.id || null;

      setFormData((prev) => ({
        ...(prev || {}),
        obrad: {
          ...obradData,
          calendarId,
        },
      }));

      setMsg(v?.warning ? `Uloženo do kalendáře. Pozor: ${v.warning}` : "Uloženo do kalendáře ✅");
    } catch (e) {
      setMsg(e?.data?.error || e.message || "Chyba uložení.");
    } finally {
      setSaving(false);
    }
  }

  function pickOtherCrew(nazev) {
    setField("posadka", nazev);
    setShowCrewPicker(false);
    setMsg("Vybrána jiná posádka – klikni Uložit do kalendáře znovu.");
  }

  const availableCrews = POSADKY.filter((p) => p.nazev !== local.posadka);

  return (
    <div>

      {/* FORCE_CAL_VISIBLE_TOP */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "10px 0" }}>
        <button
          type="button"
          onClick={handleSaveToCalendar}
          disabled={saving}
          style={{
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid #ddd",
            cursor: saving ? "not-allowed" : "pointer",
            fontWeight: 900
          }}
          title="Uloží/aktualizuje obřad v kalendáři a naváže ho na aktuální zakázku"
        >
          📅 Uložit do kalendáře
        </button>

        <button
          type="button"
          onClick={handleSaveOnlyForm}
          disabled={saving}
          style={{
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid #ddd",
            cursor: saving ? "not-allowed" : "pointer",
            fontWeight: 700
          }}
          title="Uloží jen do objednávky (bez kalendáře)"
        >
          💾 Uložit (jen v objednávce)
        </button>
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={labelStyle}>Typ obřadu</div>
        <input style={inputStyle} value={local.typ} onChange={(e) => setField("typ", e.target.value)} placeholder="např. rozloučení" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div>
          <div style={labelStyle}>Datum</div>
          <input style={inputStyle} type="date" value={local.datum} onChange={(e) => setField("datum", e.target.value)} />
        </div>
        <div>
          <div style={labelStyle}>Čas od</div>
          <input style={inputStyle} type="time" value={local.casOd} onChange={(e) => setField("casOd", e.target.value)} />
        </div>
        <div>
          <div style={labelStyle}>Čas do</div>
          <input style={inputStyle} type="time" value={local.casDo} onChange={(e) => setField("casDo", e.target.value)} />
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={labelStyle}>Místo / vystavení</div>
        <input style={inputStyle} value={local.vystav} onChange={(e) => setField("vystav", e.target.value)} placeholder="např. obřadní síň / kostel / adresa" />
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={labelStyle}>Posádka</div>
        <select style={inputStyle} value={local.posadka} onChange={(e) => setField("posadka", e.target.value)}>
          <option value="">-- vyber posádku --</option>
          {POSADKY.map((p) => (
            <option key={p.nazev} value={p.nazev}>
              Posádka {p.nazev} ({(p.lide || []).join(", ")})
            </option>
          ))}
        </select>
          
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.85 }}>Posádka (ručně – brigádníci)</div>
          <input
            style={inputStyle}
            value={local.posadkaRucne || ""}
            onChange={(e) => setField("posadkaRucne", e.target.value)}
            placeholder="např. Brigádník Jarda / externí posádka…"
          />
          <div style={{ marginTop: 6, fontSize: 12, opacity: 0.75 }}>
            Když napíšeš ručně, systém bere posádku z ručního pole (není v seznamu).
          </div>
        </div>
<div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.85 }}>Auto (SPZ)</div>
            <select
              value={(formData?.obrad?.autoSpz || "")}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                obrad: { ...(prev?.obrad || {}), autoSpz: e.target.value }
              }))}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid rgba(0,0,0,0.15)",
                background: "white"
              }}
            >
              <option value="">-- bez auta --</option>
              {AUTA_SPZ.map((spz) => (
                <option key={spz} value={spz}>{spz}</option>
              ))}
            </select>
          </div>

      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={labelStyle}>Poznámka</div>
        <input style={inputStyle} value={local.poznamka} onChange={(e) => setField("poznamka", e.target.value)} placeholder="cokoliv důležitého" />
      </div>

      {/* info box */}
      <div style={{ marginBottom: 12, padding: 10, borderRadius: 12, border: "1px solid #ddd", background: "#f7f7f7", fontSize: 13 }}>
        <b>Zakázka ID (objednavkaId):</b> {orderId || "(není)"} <br />
        <span style={{ opacity: 0.8 }}>Tip: musíš mít otevřenou zakázku z knihy, aby se currentOrderId naplnil.</span>
      </div>

      {kolize?.collisions?.length ? (
        <div style={{ border: "1px solid #f2c94c", background: "#fff7d6", padding: 12, borderRadius: 12, marginBottom: 12 }}>
          <div style={{ fontWeight: 900, marginBottom: 8 }}>
            {kolize.blocking ? "⛔ Kolize se stejnou posádkou" : "⚠️ V tomto čase už je obřad"}
          </div>
          {kolize.collisions.map((c) => (
            <div key={c.id} style={{ fontSize: 13, padding: "6px 0", borderTop: "1px dashed rgba(0,0,0,0.15)" }}>
              {c.datum} {c.casOd}-{c.casDo} | {c.posadkaNazev} | {c.zesnuly} | {c.misto}
            </div>
          ))}
        </div>
      ) : null}

      
      
      {(showCrewPicker || showAutoPicker) && (
        <div style={{ border: "1px solid #e74c3c", background: "#ffe9e6", padding: 12, borderRadius: 12, marginBottom: 12 }}>

          {showAutoPicker && (
            <div style={{ marginBottom: 12, padding: 10, borderRadius: 12, border: "1px solid rgba(0,0,0,0.12)", background: "#fff" }}>
              <div style={{ fontSize: 13, fontWeight: 900, marginBottom: 6 }}>Vyber jiné auto (SPZ):</div>

              <select
                style={inputStyle}
                value={(formData?.obrad?.autoSpz || "")}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...(prev || {}),
                    obrad: { ...(prev?.obrad || {}), autoSpz: e.target.value },
                  }))
                }
              >
                <option value="">-- bez auta --</option>
                {AUTA_SPZ.map((spz) => (
                  <option key={spz} value={spz}>{spz}</option>
                ))}
              </select>

              <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  style={btnStyle}
                  onClick={() => {
                    setShowAutoPicker(false);
                    setMsg("✅ Auto upraveno. Klikni znovu na Uložit do kalendáře.");
                  }}
                >
                  Použít auto
                </button>

                <button
                  type="button"
                  style={{ ...btnStyle, opacity: 0.9 }}
                  onClick={() => {
                    setFormData((prev) => ({
                      ...(prev || {}),
                      obrad: { ...(prev?.obrad || {}), autoSpz: "" },
                    }));
                    setShowAutoPicker(false);
                    setMsg("✅ Nastaveno bez auta. Klikni znovu na Uložit do kalendáře.");
                  }}
                >
                  Bez auta
                </button>
              </div>

              <div style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
                Tip: Auto je volitelné. Když necháš „bez auta“, nebude se kontrolovat kolize auta.
              </div>
            </div>
          )}

          {showCrewPicker && (
            <>
              <div style={{ fontWeight: 900, marginBottom: 8 }}>Vyber jinou posádku:</div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {availableCrews.map((p) => (
                  <button
                    key={p.nazev}
                    style={btnStyle}
                    onClick={() => pickOtherCrew(p.nazev)}
                    type="button"
                  >
                    Posádka {p.nazev} ({(p.lide || []).join(", ")})
                  </button>
                ))}
              </div>

              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 800, opacity: 0.85 }}>Posádka ručně (brigádníci):</div>
                <input
                  style={inputStyle}
                  value={(local.posadkaRucne || "")}
                  onChange={(e) => setField("posadkaRucne", e.target.value)}
                  placeholder="např. Brigáda: Karel + Petr"
                />
                <div style={{ fontSize: 12, marginTop: 6, opacity: 0.8 }}>
                  Když vyplníš ručně, použije se místo seznamu.
                </div>
              </div>

              <div style={{ fontSize: 12, marginTop: 10, opacity: 0.85 }}>
                Nebo změň čas a klikni znovu Uložit do kalendáře.
              </div>
            </>
          )}

        </div>
      )}


          
<div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <button style={btnStyle} onClick={handleSaveOnlyForm} disabled={saving}>
          Uložit (jen objednávka)
        </button>

        <button style={{ ...btnStyle, borderColor: "#111", background: "#111", color: "#fff" }} onClick={handleSaveToCalendar} disabled={saving}>
          {saving ? "Ukládám..." : "Uložit do kalendáře"}
        </button>

        {msg ? <div style={{ fontWeight: 800 }}>{msg}</div> : null}
      </div>
    </div>
  );
}
